import { Extension } from '@tiptap/core';

/**
 * ListStyle
 * ---------
 * Adds a `listStyle` attribute to ordered / bullet lists (rendered as
 * `data-list-style`) so the ribbon's numbering / bullet galleries and Word
 * copy-paste can choose the marker (1. / a. / A. / i. / I. / a) / (a) …,
 * • / ◦ / ▪ / – / ➢ / ✓). The markers themselves are drawn by CSS in
 * editor.css and LetterDocument.css.
 *
 * It also makes `<ol start="N">` actually start at N. The markers are CSS
 * counters, which ignore the `start` attribute, so an explicit
 * `counter-reset` is rendered for lists that don't start at 1.
 *
 * Lists without these attributes (every letter saved before this extension
 * existed) render exactly as before.
 */

export const ORDERED_LIST_STYLES = [
  'decimal', 'decimal-paren', 'decimal-parens',
  'lower-alpha', 'lower-alpha-paren', 'lower-alpha-parens',
  'upper-alpha', 'upper-alpha-paren', 'upper-alpha-parens',
  'lower-roman', 'lower-roman-paren', 'lower-roman-parens',
  'upper-roman', 'upper-roman-paren', 'upper-roman-parens'
] as const;

export const BULLET_LIST_STYLES = ['disc', 'circle', 'square', 'dash', 'arrow', 'check'] as const;

const ALLOWED = new Set<string>([...ORDERED_LIST_STYLES, ...BULLET_LIST_STYLES]);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    listStyle: {
      /** Make the current block a numbered list (if needed) and set its numbering style. */
      setOrderedListStyle: (style: string | null) => ReturnType;
      /** Make the current block a bullet list (if needed) and set its bullet style. */
      setBulletListStyle: (style: string | null) => ReturnType;
    };
  }
}

function setNearestListStyle(listType: string) {
  return (style: string | null) =>
    ({ state, tr, dispatch, chain }: any) => {
      const value = style && ALLOWED.has(style) ? style : null;
      const findList = (s: any) => {
        const { $from } = s.selection;
        for (let d = $from.depth; d > 0; d--) {
          const node = $from.node(d);
          if (node.type.name === listType) return { pos: $from.before(d), node };
          if (node.type.name === 'bulletList' || node.type.name === 'orderedList') return null;
        }
        return null;
      };

      if (!findList(state)) {
        // Not inside this list type yet: create/convert the list first, then
        // style the list the cursor ends up in.
        const toggle = listType === 'orderedList' ? 'toggleOrderedList' : 'toggleBulletList';
        return chain()
          [toggle]()
          .command(({ state: next, tr: nextTr }: any) => {
            const found = findList(next);
            if (!found) return false;
            nextTr.setNodeMarkup(found.pos, undefined, { ...found.node.attrs, listStyle: value });
            return true;
          })
          .run();
      }

      const found = findList(state);
      if (dispatch && found) {
        tr.setNodeMarkup(found.pos, undefined, { ...found.node.attrs, listStyle: value });
      }
      return true;
    };
}

export const ListStyle = Extension.create({
  name: 'listStyle',

  addGlobalAttributes() {
    return [
      {
        types: ['orderedList', 'bulletList'],
        attributes: {
          listStyle: {
            default: null,
            parseHTML: (el: HTMLElement) => {
              const v = el.getAttribute('data-list-style');
              return v && ALLOWED.has(v) ? v : null;
            },
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.listStyle ? { 'data-list-style': String(attrs.listStyle) } : {}
          }
        }
      },
      {
        types: ['orderedList'],
        attributes: {
          // Derived from `start`; never parsed, only rendered.
          listCounterStart: {
            default: null,
            parseHTML: () => null,
            renderHTML: (attrs: Record<string, unknown>) => {
              const start = Number(attrs.start);
              if (!Number.isFinite(start) || start === 1) return {};
              return { style: `counter-reset: li-counter ${Math.trunc(start) - 1}` };
            }
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setOrderedListStyle: setNearestListStyle('orderedList'),
      setBulletListStyle: setNearestListStyle('bulletList')
    } as any;
  }
});
